import { PROGRESS_FOSSID_STATUS, LOCAL_STORAGE } from "@constants/index";
import { ScanToolSearchInfoContext } from "@components/Guards/ScanToolGuard";
import { useTheme } from "@hooks/useTheme";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Communication from "../../../components/Communication/Communication";
import {
  AnalysisIcon,
  CheckAnalysisIcon,
  CheckFollowUpIcon,
  FollowUpIcon,
  LoadIdentifyIcon,
  ReadyIcon,
  ReviewIcon,
  UsageTypeCheckIcon,
  UsageTypeInputIcon,
} from "../../../components/Icons";
import { ComponentTable, ComponentTableHeader } from "../../../components/Tables";
import {
  BasicInfoSection,
  Breadcrumb,
  FulfillmentInfo,
  VerificationReportInfo,
  WorkflowProgressFossID,
} from "../../../components/UI";
import queryKeys from "../../../configs/react-query/query-key";
import { useFossidVerification } from "../../../hooks/useFossidVerification";
import { useMutationData } from "../../../hooks/useMutationData";
import useProjectMembers from "../../../hooks/useProjectMembers";
import { formatFullDateTime } from "@utils/common";
import { getLocalStorage } from "@utils/local-storage";

const FossIDVerification = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { projectSeq, sourceCodeVerificationSeq } = useParams();
  const [modifiedComponentData, setModifiedComponentData] = useState([]);
  const [requests, setRequests] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);
  const { creatorList = [] } = useContext(ScanToolSearchInfoContext) ?? {};
  const queryClient = useQueryClient();
  const { membersData: projectMembers } = useProjectMembers(projectSeq);
  const navigate = useNavigate();

  // Set preference for FossID tab when this component mounts
  useEffect(() => {
    // Safely set preference when component mounts
    try {
      localStorage.setItem("preferredTab", "fossid");
      localStorage.setItem("lastProjectSeq", projectSeq);
    } catch (e) {
      console.error("Error setting localStorage items:", e);
    }

    return () => {
      // This will run when component unmounts (including browser back)
      try {
        localStorage.setItem("preferredTab", "fossid");
        localStorage.setItem("lastProjectSeq", projectSeq);
      } catch (e) {
        console.error("Error setting localStorage items on unmount:", e);
      }
    };
  }, [projectSeq]);

  const completeAction = useMutationData(
    "/source_code_action_detail",
    {
      method: "POST",
      params: {
        actionType: "complete",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error completing action:", error);
      },
    },
  );

  const approveAction = useMutationData(
    "/source_code_action_detail",
    {
      method: "POST",
      params: {
        actionType: "approve",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error approving action:", error);
      },
    },
  );

  const rejectAction = useMutationData(
    "/source_code_action_detail",
    {
      method: "POST",
      params: {
        actionType: "reject",
      },
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error rejecting action:", error);
      },
    },
  );

  const createActionRequest = useMutationData(
    "/source_code_action_request",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error creating action request:", error);
      },
    },
  );

  const addVerificationComment = useMutationData(
    "/source_code_verification_verification_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error adding verification comment:", error);
      },
    },
  );

  const addFulfillmentComment = useMutationData(
    "/source_code_verification_fulfillment_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error adding fulfillment comment:", error);
      },
    },
  );

  const addUsageComment = useMutationData(
    "/source_code_verification_usage_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error adding usage comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const addUsageInputComment = useMutationData(
    "/source_code_usage_type_input_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error adding usage input comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const addFollowupCheckComment = useMutationData(
    "/source_code_check_follow_up_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error adding followup check comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const addAnalysisCheckComment = useMutationData(
    "/source_code_analysis_check_comment",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([queryKeys.sourceCodeAction]);
      },
      onError: (error) => {
        console.error("Error adding followup check comment:", error);
      },
      onMutate: (variables) => {
        return variables;
      },
    },
  );

  const [sourceCodeScanFollowupResults, setSourceCodeScanFollowupResults] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleEdit = () => setIsEditMode(true);
  const handleCancel = () => setIsEditMode(false);
  const handleSave = () => {
    handleSaveSourceCodeScanComponents();
    setIsEditMode(false);
  };

  const {
    // Loading state
    isLoading,
    isFetching,

    verificationStatus,
    approvalFlag,
    projectData,
    verificationData,
    projectId,
    verificationVersion,
    fetchedLicensePolicy,
    fetchedSourceCodeScanComponent,
    fetchedSourceCodeScanFollowupPlan,
    sourceCodeScanFollowupPlanUpdatedAt,
    fetchedSourceCodeScanDependency,
    fetchedSourceCodeAction,
    verificationReportsData,
    isReviewOrDoneStage,
    isNotAnalysisOrCheckAnalysisOrRequest,
    isFulfillmentOrCheckFulfillmentStage,
    fulfillmentInfoRef,
    handleUpdateStatus,
    handleMoveToReview,
    handleRejectVerification,
    handleMoveToDone,
    handleCompleteVerification,
    handleMoveToFulfillment,
    handleMoveToRequest,
    handleDecompleteFromDone,
    handleGetVerificationReportForPopup,
    handleToCompleteFulfillment,
    handleToRequestReVerification,
    handleMoveToUsageTypeCheck,
    handleMoveToUsageTypeInput,
    handleToFollowUpResult,
    handleRejectVerificationFromReview,
    handleRejectVerificationFromOnVerification,
    handleRejectUsageTypeCheck,
    handleRejectUseTypeInput,
    handleRejectUseTypeReview,
    handleSaveSourceCodeScanComponents,
  } = useFossidVerification(
    projectSeq,
    sourceCodeVerificationSeq,
    modifiedComponentData,
    sourceCodeScanFollowupResults,
  );

  // Prepare current verification data for re-upload
  const currentVerificationData =
    verificationData && Object.keys(verificationData).length > 0
      ? {
          sourceCodeVerificationSeq: verificationData.sourceCodeVerificationSeq || sourceCodeVerificationSeq,
          projectId: verificationData.projectId || projectData?.projectId,
          verificationVersion: verificationData.verificationVersion || verificationVersion,
          projectSeq: projectSeq,
          ...verificationData,
        }
      : null;

  const handleComponentDataChange = useCallback((data) => {
    setModifiedComponentData(data);
  }, []);

  // Transform API data to Communication component format
  useEffect(() => {
    if (fetchedSourceCodeAction?.requests) {
      const transformedRequests = fetchedSourceCodeAction.requests.map((request) => {
        // Find requester info from creatorList
        const requesterInfo = creatorList.find((user) => user.value === request.createrId);
        const requesterRole = requesterInfo?.roleCdName || "unknown";

        // Find assignee info from creatorList
        const assigneeInfo = creatorList.find((user) => user.value === request.assigneeId);
        const assigneeRole = assigneeInfo?.roleCdName || "unknown";

        return {
          id: request.actionSeq,
          date: formatFullDateTime(request.createdAt),
          status: request.verificationStatusCdName || "Pending",
          actionStatus: request.actionStatus,
          requester: {
            requesterId: request.createrId,
            name: request.createrName || request.createrId || "Unknown",
            role: requesterRole,
          },
          assignee: {
            assigneeId: request.assigneeId,
            name: request.assigneeName || request.assigneeId || "Unknown",
            role: assigneeRole,
          },
          description: request.actionsContent || "No description provided",
          completionRequestDate: request.completionRequestDt || request.createdAt,
          cve: request.cve,
          conversation:
            request.sourceCodeActionDetails?.map((detail) => {
              // Find user info from creatorList
              const userInfo = creatorList.find((user) => user.value === detail.createrId);
              const userRole = userInfo?.roleCdName || "Unknown";

              return {
                user: {
                  name: userInfo?.userName || userInfo?.label || "Unknown",
                  role: userRole,
                },
                status: detail.actionStatus,
                timestamp: formatFullDateTime(detail.createdAt),
                content: detail.actionContent || "",
              };
            }) || [],
        };
      });

      setRequests(transformedRequests);
    }

    // System messages from action API (CVE + Security Alert digests)
    const cveMessages = fetchedSourceCodeAction?.systemMessages || [];
    setSystemMessages(cveMessages);
  }, [fetchedSourceCodeAction, creatorList]);

  // Get current step index based on verification status
  const getCurrentStepIndex = () => {
    const statusStepMap = {
      "Request ReVerification": 0,
      "Analyzing...": 1,
      "Analysis Check": 2,
      "Request Verification": 3,
      "Usage Type Check": 4,
      "Usage Type Input": 5,
      "Review Verification": 6,
      "Standby Followup": 7,
      "Fulfillment Completed": 8,
      Done: 9,
    };
    return statusStepMap[verificationData.verificationStatusCdName] || 0;
  };

  // Check if any request has actionStatus other than "DONE"
  const shouldShowForceCompleteText = () => {
    const requests = fetchedSourceCodeAction?.requests || [];
    if (requests.length === 0) return false;
    return !requests.every((request) => (request.actionStatus || "").toUpperCase() === "DONE");
  };

  // Workflow steps configuration
  const workflowSteps = [
    { label: "Request ReVerification", icon: null, displayLabel: t("fossid-status-options.reverification") }, // No icon for this step
    { label: "Analyzing", icon: AnalysisIcon, displayLabel: t("clarity-status-options.analyzing") },
    { label: "Analysis Check", icon: CheckAnalysisIcon, displayLabel: t("fossid-status-options.analysis-check") },
    {
      label: "Load Identifications",
      icon: LoadIdentifyIcon,
      displayLabel: t("common.load-identifications"),
    },
    { label: "Usage type check", icon: UsageTypeCheckIcon, displayLabel: t("status.usageTypeCheck") },
    { label: "Usage type input", icon: UsageTypeInputIcon, displayLabel: t("status.usageTypeInput") },
    { label: "Review", icon: ReviewIcon, displayLabel: t("status.onReview") },
    { label: "Follow-up", icon: FollowUpIcon, displayLabel: t("status.standbyFollowup") },
    { label: "Check follow-up", icon: CheckFollowUpIcon, displayLabel: t("status.followupCheck") },
    { label: "Ready", icon: ReadyIcon, displayLabel: t("status.done") },
  ];

  // Helper function to get request data
  const getRequestData = (requestId, actionContent) => {
    const request = fetchedSourceCodeAction?.requests?.find((req) => req.actionSeq === requestId);

    if (!request) {
      console.error("Request not found:", requestId);
      return null;
    }

    return {
      actionSeq: request.actionSeq,
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      actionContent: actionContent,
    };
  };

  // Communication handlers
  const handleComplete = (completeData) => {
    const { requestId, message } = completeData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      completeAction.mutate(requestData);
    }
  };

  const handleApprove = (approvalData) => {
    const { requestId, message } = approvalData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      approveAction.mutate(requestData);
    }
  };

  const handleReject = (rejectionData) => {
    const { requestId, message } = rejectionData;
    const requestData = getRequestData(requestId, message);
    if (requestData) {
      rejectAction.mutate(requestData);
    }
  };

  const handleAddRequest = (requestData) => {
    // Convert date to ISO format
    const completionRequestDt = new Date(requestData.completionRequestDate).toISOString();

    const requestPayload = {
      assigneeId: requestData.assignee,
      completionRequestDt: completionRequestDt,
      actionsContent: requestData.requestMessage,
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      ...(requestData.cve && { cve: requestData.cve }),
      ...(requestData.actionSeq && { actionSeq: requestData.actionSeq }),
    };

    createActionRequest.mutate(requestPayload);
  };

  const handleAddComment = (commentData) => {
    // Check verification status to determine which API to call
    if (verificationData?.verificationStatusCd === "STANDBY_FOLLOWUP") {
      addFulfillmentComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "ON_REVIEW") {
      addVerificationComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Check"
    ) {
      addUsageComment.mutate(commentData);
    } else if (
      verificationData?.verificationStatusCd === "ON_VERIFICATION" &&
      verificationData?.verificationStatusCdName === "Usage Type Input"
    ) {
      addUsageInputComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "FOLLOWUP_CHECK") {
      addFollowupCheckComment.mutate(commentData);
    } else if (verificationData?.verificationStatusCd === "ANALYSIS_CHECK") {
      addAnalysisCheckComment.mutate(commentData);
    } else {
      console.error("Invalid verification status:", verificationData?.verificationStatusCd);
    }
  };

  return (
    <div className="min-h-full" data-testid="foss-id-verification-page">
      {/* Header with Breadcrumbs */}
      <div className="p-4" data-testid="fossid-verification-header">
        <div className="space-y-3">
          <Breadcrumb
            data-testid="fossid-verification-breadcrumb"
            onBackClick={() => {
              navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
            }}
            items={[
              { label: t("navbar.project-list"), href: "/projects" },
              {
                label: t("navbar.project-details"),
                onClick: () => {
                  navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
                },
              },
              { label: t("navbar.fossid-verification") },
            ]}
          />
          <div>
            <h1
              className={`text-2xl font-bold ${colors.textPrimary}`}
              data-testid="fossid-verification-page-title"
            >
              {verificationData.realFileName}
            </h1>{" "}
          </div>
        </div>
      </div>

      <div>
        {/* Basic Information */}
        <BasicInfoSection
          data-testid="fossid-verification-basic-info"
          data={{
            ...verificationData,
            projectName: projectData?.projectName || verificationData?.projectName,
          }}
          colors={colors}
          t={t}
          type={"fossid"}
        />

        {/* Workflow Progress */}
        <div className={`px-6 py-6 rounded-2xl bg-[${colors.bgPrimaryColor}]`} data-testid="fossid-verification-workflow">
          <WorkflowProgressFossID
            steps={workflowSteps}
            approvalFlag={approvalFlag}
            currentStep={getCurrentStepIndex()}
            projectManagerId={projectData?.projectManagerId}
            onMoveToUsageTypeCheck={handleMoveToUsageTypeCheck}
            onMoveToUsageTypeInput={handleMoveToUsageTypeInput}
            onMoveToReview={handleMoveToReview}
            onMoveToLoadIdentifications={handleMoveToRequest}
            onRequestReVerification={handleToRequestReVerification}
            onMoveToFollowUp={handleMoveToFulfillment}
            onMoveToFollowCheckUp={handleToCompleteFulfillment}
            onMoveToDone={handleMoveToDone}
            onCompleteVerification={handleCompleteVerification}
            onDecompleteFromDone={handleDecompleteFromDone}
            onUpdateStatus={handleUpdateStatus}
            onRejectVerification={handleRejectVerification}
            onRejectUsageTypeCheck={handleRejectUsageTypeCheck}
            onRejectUsageTypeInput={handleRejectUseTypeInput}
            onRejectUsageTypeReview={handleRejectUseTypeReview}
            onToFollowUpResult={handleToFollowUpResult}
            onRejectVerificationFromReview={handleRejectVerificationFromReview}
            onRejectVerificationFromOnVerification={handleRejectVerificationFromOnVerification}
            currentVerificationData={currentVerificationData}
            forceCompleteText={shouldShowForceCompleteText()}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6 mt-6">
        {/* Communication Section */}
        <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="foss-id-verification-communication">
          <Communication
            requests={requests}
            systemMessages={systemMessages}
            verificationData={verificationData}
            verificationType="source_code"
            verificationSeq={sourceCodeVerificationSeq}
            onComplete={handleComplete}
            onApprove={handleApprove}
            onReject={handleReject}
            onAddRequest={handleAddRequest}
            onAddComment={handleAddComment}
            isAddCommentPending={
              addVerificationComment.isPending || addFulfillmentComment.isPending || addUsageComment.isPending
            }
            projectMembers={projectMembers}
            projectSeq={projectSeq}
            projectData={projectData}
            creatorList={creatorList}
          />
        </div>

        {/* Component List */}
        <div data-testid="foss-id-verification-component-table">
        <ComponentTable
          data={fetchedSourceCodeScanComponent}
          licensePolicyData={fetchedLicensePolicy}
          onDataChange={handleComponentDataChange}
          isEditingAllowed={verificationStatus && verificationData.verificationStatusCdName === "Usage Type Input"}
          seq="sourceCodeScanComponentSeq"
          onSave={handleSave}
          className="p-4"
          allowVexReview
          showShieldFilter
          vexContext={{
            scanToolCd: "fossid",
            verificationSeq: sourceCodeVerificationSeq,
            projectSeq,
            projectManagerId: projectData?.projectManagerId,
            verificationStatusCd: verificationData?.verificationStatusCd,
          }}
        >
          <ComponentTableHeader
            title={t("common.component-list")}
            count={fetchedSourceCodeScanComponent?.length || 0}
            colors={colors}
            t={t}
          />
        </ComponentTable>
        </div>

        {/* Component Dependency List */}
        {(verificationData?.verificationStatusCd === PROGRESS_FOSSID_STATUS.VERIFICATION ||
          verificationData?.verificationStatusCd === PROGRESS_FOSSID_STATUS.REVIEW ||
          verificationData?.verificationStatusCd === PROGRESS_FOSSID_STATUS.FULFILLMENT ||
          verificationData?.verificationStatusCd === PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT ||
          verificationData?.verificationStatusCd === PROGRESS_FOSSID_STATUS.DONE) && (
          <div data-testid="foss-id-verification-dependency-table">
          <ComponentTable
            data={fetchedSourceCodeScanDependency}
            licensePolicyData={fetchedLicensePolicy}
            isFossIDDependency={true}
            seq="sourceCodeScanDependencySeq"
            className="p-4"
          >
            <ComponentTableHeader
              title={t("project.fossid.component-dependency-list")}
              count={fetchedSourceCodeScanDependency?.length || 0}
              isEditMode={false}
              isEditingAllowed={false}
              colors={colors}
              t={t}
            />
          </ComponentTable>
          </div>
        )}

        {/* Fulfillment List */}
        {(isReviewOrDoneStage || isFulfillmentOrCheckFulfillmentStage) && (
          <FulfillmentInfo
            key={sourceCodeScanFollowupPlanUpdatedAt}
            data-testid="fossid-verification-fulfillment-info"
            loggedInDisplayName={creatorList.find((user) => user.userId === getLocalStorage(LOCAL_STORAGE.USER_ID))}
            ref={fulfillmentInfoRef}
            fulfillmentData={fetchedSourceCodeScanFollowupPlan}
            verificationStatus={verificationStatus}
            projectManagerId={projectData?.projectManagerId}
          />
        )}

        {/* Verification Report */}
        {isNotAnalysisOrCheckAnalysisOrRequest && (
          <VerificationReportInfo
            data-testid="fossid-verification-report-info"
            projectId={projectId}
            projectManagerId={projectData?.projectManagerId}
            verificationVersion={verificationVersion}
            handleGetVerificationReportForPopup={handleGetVerificationReportForPopup}
            verificationReportsData={verificationReportsData}
            verificationToolCd="FOSSID"
            verificationSeq={sourceCodeVerificationSeq}
          />
        )}
      </div>
    </div>
  );
};

export default FossIDVerification;
